import { Box, Flex, Input, InputProps } from '@chakra-ui/react';
import { ChangeEvent, FocusEvent, forwardRef, ReactNode, useEffect, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { B2bFieldLayout } from '../B2bFieldLayout';
import {
  B2B_FIELD_CONTROL_BORDER_RADIUS,
  B2B_FIELD_CONTROL_BORDER_WIDTH,
  B2B_FIELD_CONTROL_HEIGHT_PX,
  B2B_FIELD_CONTROL_MAX_WIDTH,
  B2B_FIELD_CONTROL_PADDING_X,
  B2B_FIELD_CONTROL_PADDING_Y,
} from '../b2bFormControlTokens';
import { b2bFieldFocusVisible, colors } from 'styles/colors';
import { DIALING_CODE_IRAQ } from 'utils/constants';
import {
  e164IraqiToDisplayNational,
  getNormalizeIraqiPhoneIntlMessage,
  normalizeIraqiPhone,
  type NormalizeIraqiPhoneErrorCode,
} from 'utils/iraqiPhone';

export type FieldPhoneProps = Omit<
  InputProps,
  'id' | 'value' | 'defaultValue' | 'type' | 'inputMode'
> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  /** External error (e.g. server / form schema); shown together with normalization errors. */
  error?: ReactNode;
  /** Canonical value from form state: `+964` + 10-digit national. */
  normalizedValue?: string;
  /**
   * **Blur-only contract (TZ-284):** parent receives a normalized `+964…` string when
   * `normalizeIraqiPhone` succeeds on **blur**, or `null` if the blurred value is empty or invalid.
   * Typing does not emit — avoids false positives while the user is editing.
   */
  onNormalizedValueChange: (value: string | null) => void;
  /** Split layout: `rtl` puts +964 on inline-end (right in Arabic); `ltr` mirrors for English. */
  dir?: 'ltr' | 'rtl';
};

/**
 * B2B phone field: friendly display (`07…`, `7…`, `964…`, etc.), stored/submitted value via
 * {@link normalizeIraqiPhone} only on blur. Reuses `utils/iraqiPhone` (no duplicate normalization).
 * Split control: Iraq +964 prefix + national digits (RTL layout, Figma).
 */
export const FieldPhone = forwardRef<HTMLInputElement, FieldPhoneProps>(
  (
    {
      id: idProp,
      label,
      hint,
      error: errorProp,
      normalizedValue = '',
      onNormalizedValueChange,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid: isInvalidProp,
      onBlur: onBlurFromProps,
      onChange: onChangeFromProps,
      placeholder: placeholderProp,
      ...restInputProps
    },
    ref,
  ) => {
    const intl = useIntl();
    const autoId = useId();
    const fieldId = idProp ?? `b2b-field-phone-${autoId}`;
    const placeholder =
      placeholderProp ??
      intl.formatMessage({
        id: 'b2b.registration.field.phoneNumber.placeholder',
        defaultMessage: '077xxxxxxxxxx',
      });

    const [displayValue, setDisplayValue] = useState(() =>
      e164IraqiToDisplayNational(normalizedValue),
    );
    const [normalizeErrorCode, setNormalizeErrorCode] =
      useState<NormalizeIraqiPhoneErrorCode | null>(null);

    useEffect(() => {
      setDisplayValue(e164IraqiToDisplayNational(normalizedValue));
    }, [normalizedValue]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(e.target.value);
      setNormalizeErrorCode(null);
      onChangeFromProps?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlurFromProps?.(e);
      const result = normalizeIraqiPhone(e.target.value);
      if (result.ok) {
        setNormalizeErrorCode(null);
        onNormalizedValueChange(result.e164);
        setDisplayValue(e164IraqiToDisplayNational(result.e164));
        return;
      }
      setNormalizeErrorCode(result.error);
      onNormalizedValueChange(null);
    };

    const normalizeErrorNode =
      normalizeErrorCode != null ? (
        <FormattedMessage
          key={normalizeErrorCode}
          {...getNormalizeIraqiPhoneIntlMessage(normalizeErrorCode, 'b2b')}
        />
      ) : undefined;

    const mergedError = errorProp ?? normalizeErrorNode;
    const isInvalid = isInvalidProp ?? Boolean(mergedError);
    const borderColor = isInvalid ? colors.red : 'dark.D150';

    return (
      <B2bFieldLayout
        fieldId={fieldId}
        label={label}
        hint={hint}
        error={mergedError}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
      >
        <Flex
          dir='ltr'
          w='full'
          maxW={B2B_FIELD_CONTROL_MAX_WIDTH}
          h={B2B_FIELD_CONTROL_HEIGHT_PX}
          minH={B2B_FIELD_CONTROL_HEIGHT_PX}
          borderRadius={B2B_FIELD_CONTROL_BORDER_RADIUS}
          borderWidth={B2B_FIELD_CONTROL_BORDER_WIDTH}
          borderStyle='solid'
          borderColor={borderColor}
          overflow='hidden'
          align='stretch'
          cursor={isDisabled ? 'not-allowed' : 'text'}
          opacity={isDisabled ? 0.6 : 1}
          transition='border-color 0.15s ease, box-shadow 0.15s ease'
          _hover={
            isDisabled || isReadOnly
              ? undefined
              : {
                  borderColor: isInvalid ? colors.red : 'dark.D200',
                }
          }
          _focusWithin={
            isDisabled || isReadOnly
              ? undefined
              : isInvalid
              ? {
                  borderColor: colors.red,
                  boxShadow: `0 0 0 1px ${colors.red}`,
                }
              : {
                  ...b2bFieldFocusVisible,
                }
          }
        >
          <Flex
            align='center'
            gap={2}
            px={B2B_FIELD_CONTROL_PADDING_X}
            py={B2B_FIELD_CONTROL_PADDING_Y}
            h='100%'
            minH={B2B_FIELD_CONTROL_HEIGHT_PX}
            bg={colors.b2b.fieldPhonePrefixBg}
            color={colors.b2b.fieldPhonePrefixFg}
            fontWeight='600'
            fontSize='md'
            lineHeight='1.25'
            flexShrink={0}
            borderInlineEndWidth='1px'
            borderInlineEndStyle='solid'
            borderInlineEndColor='dark.D150'
            aria-hidden='true'
            dir='ltr'
            sx={{ unicodeBidi: 'isolate' }}
          >
            {DIALING_CODE_IRAQ}
          </Flex>
          <Box flex='1' minW={0} bg='white' alignSelf='stretch'>
            <Input
              id={fieldId}
              ref={ref}
              type='tel'
              inputMode='numeric'
              autoComplete='tel'
              dir='ltr'
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              {...restInputProps}
              textAlign='left'
              _placeholder={{ color: 'dark.D400', textAlign: 'left' }}
              color='dark.D800'
              h='100%'
              minH={0}
              border='none'
              borderRadius={0}
              px={B2B_FIELD_CONTROL_PADDING_X}
              py={B2B_FIELD_CONTROL_PADDING_Y}
              fontSize='md'
              _focusVisible={{ boxShadow: 'none' }}
            />
          </Box>
        </Flex>
      </B2bFieldLayout>
    );
  },
);

FieldPhone.displayName = 'FieldPhone';
